import { ImagesIcon } from '@phosphor-icons/react'
import type React from 'react'

import type {
  LockedAttachmentGalleryItem,
  LockedAttachmentSource,
  PaymentStatus,
} from '../../types'

import GalleryThumbnail from './GalleryThumbnail'
import type { CardVariant } from './MediaShell'
import SingleThumbnail from './SingleThumbnail'

export interface CardThumbnailProps {
  variant: CardVariant
  gallery: LockedAttachmentGalleryItem[]
  title?: string
  showLocked: boolean
  paymentStatus?: PaymentStatus
  /** Single-asset props — ignored when the card renders as a gallery. */
  mimeType: string
  thumbnailUrl?: string
  source?: LockedAttachmentSource
  containedImage?: boolean
  /** Top-right overlay (e.g. Composer dismiss X). Rendered inside the thumbnail. */
  topRight?: React.ReactNode
}

/** True when the card should render as a mixed-media carousel (2+ items). */
export const isGalleryCard = (
  gallery: LockedAttachmentGalleryItem[]
): boolean => gallery.length >= 2

/**
 * The body-row icon a card shows when it renders as a gallery — `CardBody`
 * otherwise auto-detects the icon from `mimeType`. Returned as a ready node so
 * every card shares the same size/tone treatment.
 */
export const galleryBodyIcon = (variant: CardVariant): React.ReactNode => (
  <ImagesIcon
    className={
      variant === 'dark'
        ? 'size-5 shrink-0 text-white/55'
        : 'size-5 shrink-0 text-black/55'
    }
  />
)

/**
 * Owns the single-vs-gallery thumbnail decision shared by Composer /
 * `LockedAttachment.Media` cards: renders `GalleryThumbnail` for a 2+ item `gallery`,
 * else `SingleThumbnail` for the single asset. Callers pass the superset of
 * props; the unused half is ignored by whichever branch renders.
 */
const CardThumbnail: React.FC<CardThumbnailProps> = ({
  variant,
  gallery,
  title,
  showLocked,
  paymentStatus,
  mimeType,
  thumbnailUrl,
  source,
  containedImage,
  topRight,
}) =>
  isGalleryCard(gallery) ? (
    <GalleryThumbnail
      variant={variant}
      gallery={gallery}
      title={title}
      showLocked={showLocked}
      paymentStatus={paymentStatus}
      topRight={topRight}
    />
  ) : (
    <SingleThumbnail
      variant={variant}
      mimeType={mimeType}
      thumbnailUrl={thumbnailUrl}
      title={title}
      source={source}
      showLocked={showLocked}
      paymentStatus={paymentStatus}
      topRight={topRight}
      containedImage={containedImage}
    />
  )

export default CardThumbnail
